import { useQuery, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { Plus, Trash2 } from "lucide-react";
import { Link, useParams } from "react-router";
import type { Id } from "@convex/_generated/dataModel";
import { Breadcrumb, Button, PageContainer, PageHeader } from "@repo/ui";
import { libelleApplication } from "@repo/utils";
import { arianeOrganisation, libelleSection } from "../../lib/ariane";

export function GroupesList() {
  const { id } = useParams<{ id: string }>();
  const groupes = useQuery(
    api.superAdmin.listerGroupes,
    id ? { organisationId: id as Id<"organisations"> } : "skip",
  );
  const org = useQuery(
    api.superAdmin.obtenirOrganisation,
    id ? { organisationId: id as Id<"organisations"> } : "skip",
  );
  const supprimer = useMutation(api.superAdmin.supprimerGroupe);

  if (groupes === undefined) {
    return <div className="flex justify-center p-16"><div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  return (
    <PageContainer>
      <Breadcrumb items={arianeOrganisation(id, org?.nom, "groupes")} />
      <PageHeader
        titre={libelleSection("groupes")}
        actions={
          <Button asChild>
            <Link to={`/organisations/${id}/groupes/creer`}>
              <Plus size={14} />
              Nouveau groupe
            </Link>
          </Button>
        }
      />

      <div className="space-y-3">
        {groupes.map((g) => (
            <div key={g._id} className="bg-card border border-charte-line rounded-surface p-4 flex items-center justify-between transition-colors duration-150 hover:border-charte-line-accent">
            <div>
              <span className="font-medium">{g.nom}</span>
              <p className="text-xs text-muted-foreground mt-0.5">
                {g.applicationsAutorisees
                  ? g.applicationsAutorisees.map(libelleApplication).join(" · ")
                  : "Applications de l'organisation"}
              </p>
            </div>
            <div className="flex gap-2">
              <Button asChild variant="outline" size="sm">
                <Link to={`/organisations/${id}/groupes/${g._id}/modifier`}>
                  Modifier
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to={`/organisations/${id}/users?groupId=${g._id}`}>
                  Utilisateurs
                </Link>
              </Button>
              <Button
                variant="outline"
                size="icon-sm"
                aria-label={`Supprimer le groupe ${g.nom}`}
                className="text-destructive border-destructive/30"
                onClick={() => {
                  if (confirm(`Supprimer le groupe "${g.nom}" ?`)) void supprimer({ groupId: g._id });
                }}
              >
                <Trash2 size={12} />
              </Button>
            </div>
          </div>
        ))}

        {groupes.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">Aucun groupe dans cette organisation.</div>
        )}
      </div>
    </PageContainer>
  );
}
